/**
 * Ekipteki çalışanların henüz tamamlamadığı SOP ve eğitim atamaları.
 *
 * Atama kuralı ve "tamamlandı" tanımı projenin ortak kaynağından gelir
 * (utils/assignmentDeadlines): atamalar kişiye değil role yapılır, SOP okundu
 * onayı `userSOPReadConfirmations`, eğitim ilerlemesi `userEducationProgress`
 * koleksiyonunda tutulur. Böylece bu panel, çalışanın kendi profilinde ve
 * giriş uyarılarında gördüğü listeyle aynı kuralı kullanır.
 */

import { useCallback, useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import {
  DUE_DATE_FIELD,
  daysUntil,
  describeDeadline,
  employeesForGroups,
  fetchCompletionMatrix,
  isOverdue,
} from '../../utils/assignmentDeadlines';

const EMPTY = {
  rows: [],
  summary: {
    teamSize: 0,
    pendingPeople: 0,
    pendingSops: 0,
    pendingEducations: 0,
    overdue: 0,
  },
};

/** Son tarihi yakın olan önce; tarihi olmayanlar sona. */
const sortByDeadline = (items) =>
  [...items].sort((a, b) => {
    if (a.daysLeft === null) return 1;
    if (b.daysLeft === null) return -1;
    return a.daysLeft - b.daysLeft;
  });

export const useTeamTraining = (workers, { enabled = true } = {}) => {
  const [data, setData] = useState({ ...EMPTY, loading: false, error: null });

  const load = useCallback(async () => {
    if (!enabled || workers.length === 0) {
      setData({ ...EMPTY, loading: false, error: null });
      return;
    }

    setData((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const [sopsSnap, educationsSnap, matrix] = await Promise.all([
        getDocs(collection(db, 'SOPs')),
        getDocs(collection(db, 'IndicationEducations')),
        fetchCompletionMatrix(),
      ]);

      const assignments = [
        ...sopsSnap.docs.map((d) => ({ id: d.id, ...d.data(), assignmentType: 'SOP' })),
        // Eğitimlerde yalnızca video içerenler gerçek atama sayılır (ProfilePage kuralı)
        ...educationsSnap.docs
          .map((d) => ({ id: d.id, ...d.data(), assignmentType: 'Eğitim' }))
          .filter((education) => education.videoUrl),
      ];

      // Ekip listesi sayfadan gelir; atamanın hedefini belirleyen rol bilgisi
      // Employees kaydındadır. Matris yalnızca aktif çalışanları döndürdüğü
      // için ayrılmış kişiler listeye girmez.
      const employeeById = new Map(matrix.employees.map((e) => [e.id, e]));
      const teamRecords = workers.map((worker) => employeeById.get(worker.id)).filter(Boolean);

      const pendingByEmployee = new Map();
      assignments.forEach((assignment) => {
        const completedBy =
          assignment.assignmentType === 'SOP'
            ? matrix.completedBySop.get(assignment.id)
            : matrix.completedByEducation.get(assignment.id);

        employeesForGroups(teamRecords, assignment.group).forEach((employee) => {
          if (completedBy?.has(employee.id)) return;

          const dueDate = assignment[DUE_DATE_FIELD] || null;
          const list = pendingByEmployee.get(employee.id) || [];
          list.push({
            id: assignment.id,
            type: assignment.assignmentType,
            name: assignment.name || assignment.title || 'İsimsiz atama',
            dueDate,
            daysLeft: daysUntil(dueDate),
            overdue: isOverdue(dueDate),
            deadlineLabel: describeDeadline(dueDate),
          });
          pendingByEmployee.set(employee.id, list);
        });
      });

      // Yalnızca eksiği olanlar listelenir: panel bir uyarı listesidir.
      const rows = teamRecords
        .filter((employee) => (pendingByEmployee.get(employee.id) || []).length > 0)
        .map((employee) => {
          const items = sortByDeadline(pendingByEmployee.get(employee.id));
          return {
            id: employee.id,
            name:
              `${employee.eName || ''} ${employee.eSurname || ''}`.trim() || 'İsimsiz çalışan',
            role: employee.role || '—',
            items,
            sopCount: items.filter((item) => item.type === 'SOP').length,
            educationCount: items.filter((item) => item.type === 'Eğitim').length,
            overdueCount: items.filter((item) => item.overdue).length,
            nextDeadline: items.find((item) => item.daysLeft !== null) || null,
          };
        })
        .sort(
          (a, b) =>
            b.overdueCount - a.overdueCount ||
            b.items.length - a.items.length ||
            a.name.localeCompare(b.name, 'tr')
        );

      setData({
        rows,
        summary: {
          teamSize: teamRecords.length,
          pendingPeople: rows.length,
          pendingSops: rows.reduce((sum, row) => sum + row.sopCount, 0),
          pendingEducations: rows.reduce((sum, row) => sum + row.educationCount, 0),
          overdue: rows.reduce((sum, row) => sum + row.overdueCount, 0),
        },
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Eğitim/SOP durumu alınamadı:', error);
      setData({ ...EMPTY, loading: false, error: 'Eğitim ve SOP durumu yüklenemedi.' });
    }
  }, [workers, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...data, reload: load };
};

export default useTeamTraining;
